import { useRef } from 'react'
import { useRequest } from 'ahooks'

import { Table } from '@sar/component'
import { UploadImage, UploadFile } from 'components'

import { Spin } from 'antd'
import { ProForm, ProFormText, ProFormSelect, ProFormDigit } from '@ant-design/pro-components'

export default function ({ app, store, navigator }) {
  const [ form ] = ProForm.useForm()
  const [ fileType ] = React.useMemo(() => [ store.config.getConstantPairs('FileType') ], [])

  const emps = useRequest<any, any>(() => store.externals.api.get(`/emp/all`), {
    staleTime: 3000,
    cacheKey: `/emp/all`,
  })

  const customer = useRequest(async () => {
    const result = await store.externals.api.get(`/customers/potential`, {
      params: {
        page: 1,
        size: 999999999,
      }
    })

    return result.data.map(elm => Object.assign(elm, {
      name: `${elm.name}（${elm.contactNumber}）`
    }))
  })

  const handleUpload = file => {
    return store.oss.client.multipartUpload(`${store.externals.nonce()}.${file.name.split('.').pop()}`, file).then(
      res => `//${store.oss.endpoint}/${res.name}`
    )
  }

  const handleSubmit = useRequest<any, any>(data => {
    const params = R.omit([
        fileType.receiptVoucher
      , fileType.quotationFile
      , fileType.blueprint
      , fileType.measurement
      , fileType.autograph
      , fileType.chatRecords
    ], data)

    return store.externals.api.post(`/orders/complete`, Object.assign(params, {
      fileManageList: R.flatten([
        data[fileType.receiptVoucher]?.map(elm => ({ url: elm.url, fileName: elm.filename, type: fileType.receiptVoucher })),
        data[fileType.quotationFile]?.map(elm => ({ url: elm.url, fileName: elm.filename, type: fileType.quotationFile })),
        data[fileType.blueprint]?.map(elm => ({ url: elm.url, fileName: elm.filename, type: fileType.blueprint })),
        data[fileType.measurement]?.map(elm => ({ url: elm.url, fileName: elm.filename, type: fileType.measurement })),
        data[fileType.autograph]?.map(elm => ({ url: elm.url, fileName: elm.filename, type: fileType.autograph })),
        data[fileType.chatRecords]?.map(elm => ({ url: elm.url, fileName: elm.filename, type: fileType.chatRecords })),
      ]).filter(R.identity)
    }))
  }, {
    manual: true,
  })

  return <div className='p-4 m-4 bg-white'>
    <ProForm
      grid
      form={form}
      rowProps={{ gutter: 24 }}
      layout='vertical'
      className='max-w-[60rem]'
      onFinish={data => new Promise((resolve, reject) => {
        store.externals.api.post(`/orders/check`, data).then(res => {
          if (res) {
            app.modal.confirm({
              title: '温馨提示',
              content: '检测到已有相同姓名、电话、地址的订单，您确定要继续提交吗？',
              centered: true,
              maskClosable: false,
              onCancel: () => reject(),
              onOk: () => handleSubmit.runAsync(data).then(() => resolve()).then(() => navigator.to(`/order/list`, '订单列表')),
            })
          }

          else {
            app.modal.confirm({
              title: '温馨提示',
              content: '您确定要提交当前订单吗？',
              centered: true,
              maskClosable: false,
              onCancel: () => reject(),
              onOk: () => handleSubmit.runAsync(data).then(() => resolve()).then(() => navigator.to(`/order/list`, '订单列表')),
            })
          }
        })
      })}
    >
      <ProFormSelect
        showSearch
        name='customerId'
        label='客户'
        width='xl'
        colProps={{ xs: 12 }}
        options={customer.data}
        rules={[ { required: true, message: '请选择客户' } ]}
        fieldProps={{ optionFilterProp: 'name', fieldNames: { label: 'name', value: 'id' }, loading: customer.loading }}
      />

      <ProFormText
        name='customerAddress'
        label='地址'
        colProps={{ xs: 12 }}
        rules={[ { required: true, message: '请输入地址' } ]}
      />

      <ProFormText
        name='customerPhone'
        label='联系方式'
        colProps={{ xs: 12 }}
        rules={[ { required: true, message: '请输入联系方式' }, { pattern: /^1\d{10}$/, message: '请输入正确的电话号码' } ]}
      />

      <ProFormSelect
        showSearch
        name='saleId'
        label='跟单员'
        width='xl'
        colProps={{ xs: 12 }}
        debounceTime={100000}
        options={emps.data}
        fieldProps={{ optionFilterProp: 'nickname', fieldNames: { label: 'nickname', value: 'id' }, loading: emps.loading }}
      />

      <ProFormSelect
        showSearch
        name='designerId'
        label='设计师'
        width='xl'
        colProps={{ xs: 12 }}
        debounceTime={100000}
        options={emps.data}
        fieldProps={{ optionFilterProp: 'nickname', fieldNames: { label: 'nickname', value: 'id' }, loading: emps.loading }}
      />

      <ProFormSelect
        showSearch
        name='quoteId'
        label='报价员'
        width='xl'
        colProps={{ xs: 12 }}
        debounceTime={100000}
        options={emps.data}
        fieldProps={{ optionFilterProp: 'nickname', fieldNames: { label: 'nickname', value: 'id' }, loading: emps.loading }}
      />

      <ProFormSelect
        showSearch
        name='type'
        label='生产类型'
        width='xl'
        colProps={{ xs: 12 }}
        rules={[ { required: true, message: '请选择生产类型' } ]}
        valueEnum={store.config.getConstantEnums(`OrderType`)}
      />

      <ProFormDigit
        name='totalAmount'
        label='货款总额'
        width='xl'
        min={0}
        colProps={{ xs: 12 }}
        fieldProps={{ precision: 2 }}
        rules={[ { required: true, message: '请填写货款总额' } ]}
      />

      <ProFormDigit
        name='depositAmount'
        label='订金金额'
        width='xl'
        min={0}
        colProps={{ xs: 12 }}
        fieldProps={{ precision: 2 }}
        rules={[ { required: true, message: '请填写订金金额' } ]}
      />

      <ProFormDigit
        name='paidAmount'
        label='实付金额'
        width='xl'
        min={0}
        colProps={{ xs: 12 }}
        fieldProps={{ precision: 2 }}
        rules={[ { required: true, message: '请填写实付金额' } ]}
      />

      <ProFormSelect
        name='payStatus'
        label='收款方式（如预设不符合可自填）'
        width='xl'
        rules={[ { required: true, message: '请填写收款方式' } ]}
        colProps={{ xs: 12 }}
        fieldProps={{ mode: 'tags' }}
        valueEnum={store.config.getConstantEnums(`PaymentType`)}
        convertValue={value => R.is(Array, value) ? value : value?.split(',') ?? []}
        transform={value => R.is(Array, value) ? value.join(',') : value}
      />

      <UploadImage
        colProps={{ xs: 24 }}
        max={10000}
        name={fileType.receiptVoucher}
        label='上传收款凭证'
        help='建议尺寸800*800px'
        fieldProps={{ upload: handleUpload }}
      />

      <UploadFile
        colProps={{ xs: 24 }}
        max={10}
        name={fileType.quotationFile}
        label='报价xls文件存档'
        fieldProps={{ upload: handleUpload }}
      />

      <UploadImage
        colProps={{ xs: 24 }}
        max={10000}
        name={fileType.blueprint}
        label='上传确认下单图纸'
        help='建议尺寸800*800px'
        fieldProps={{ upload: handleUpload }}
      />

      <UploadImage
        colProps={{ xs: 24 }}
        max={10000}
        name={fileType.measurement}
        label='上传现场量尺照片'
        help='建议尺寸800*800px'
        fieldProps={{ upload: handleUpload }}
      />

      <UploadImage
        colProps={{ xs: 24 }}
        max={10000}
        name={fileType.autograph}
        label='上传客户签字图纸照片'
        help='建议尺寸800*800px'
        fieldProps={{ upload: handleUpload }}
      />

      <UploadFile
        colProps={{ xs: 24 }}
        max={10}
        name={fileType.chatRecords}
        label='上传聊天记录及电话录音'
        fieldProps={{ upload: handleUpload }}
      />
    </ProForm>
  </div>
}
